import React, { useRef, useState } from "react";
import { useQuery as useConvexQuery, useMutation } from "convex/react";
import { refs } from "../../convex/apiRefs";
import { pairVersions } from "./jevViewerModel";
import { KNOWLEDGE_HELP } from "./jevHelp";
import { Tooltip } from "./JevViewer";
import "./jev.css";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// "What Jev knows" (PRED-016 part 4, 2026-10-09) — the living knowledge base: the
// current version, earlier ones to roll back to, rebuilds the checks held back, the
// nightly alerts, and the roll-back / release requests. A request is applied in Mongo
// by the Atlas trigger Scheduled_JevKnowledgeRequests within ~15 minutes; this window
// only ever writes the request (convex/scheduler/jevKnowledge.ts).
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const fmt = (ms) => (ms ? new Date(ms).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const num = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US"));
const H = ({ label, as = "h3" }) => React.createElement(as, { className: "jev-help", "data-tip": KNOWLEDGE_HELP[label] }, label);

function VersionTable({ rows, action, busy }) {
  if (!rows.length) return <div className="jev-empty">None.</div>;
  return (
    <div className="jev-tablewrap">
      <table>
        <thead>
          <tr>
            {["As of", "Jobs", "Crews", "Checks", "Built"].map((h) => (
              <th key={h} className="jev-help" data-tip={KNOWLEDGE_HELP[h]}>{h}</th>
            ))}
            <th>Versions</th>
            {action ? <th /> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.createdAt}-${r.ref?.id}-${r.crew?.id}`}>
              <td>{r.asOf || "—"}</td>
              <td>{num(r.ref?.totalJobs)}</td>
              <td>{num(r.crew?.crews)}</td>
              <td>
                {r.ref?.checksOk ? "✓" : r.ref?.problems?.length ? <span className="jev-note">{r.ref.problems.join("; ")}</span> : "—"}
              </td>
              <td>
                {fmt(r.createdAt)} <span className="jev-note">{(r.ref?.builtBy || "").replace(/^JevKnowledge /, "")}</span>
              </td>
              <td className="jev-note">
                {r.ref?.id || "—"}
                <br />
                {r.crew?.id || "—"}
              </td>
              {action ? (
                <td>
                  {r.canUse ? (
                    <button type="button" className="jev-btn" disabled={busy} onClick={() => action(r)}>
                      {r.status === "rejected" ? "Use this" : "Roll back to this"}
                    </button>
                  ) : null}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function JevKnowledge() {
  const data = useConvexQuery(refs.jevKnowledgeOverview, {});
  const requestRollback = useMutation(refs.jevKnowledgeRequestRollback);
  const requestRelease = useMutation(refs.jevKnowledgeRequestRelease);
  const rootRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  if (data === undefined) {
    return (
      <div className="jev-root" ref={rootRef}>
        <div className="jev-empty">Loading…</div>
      </div>
    );
  }
  const rows = pairVersions(data.versions);
  const current = rows.filter((r) => r.status === "current");
  const previous = rows.filter((r) => r.status === "previous");
  const held = rows.filter((r) => r.status === "rejected");
  const pending = data.requests.find((q) => q.status === "pending");

  const run = async (fn, args, done) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn(args);
      setMsg(done);
    } catch (err) {
      setMsg(String(err?.data || err?.message || err));
    } finally {
      setBusy(false);
    }
  };
  const rollback = (r) => {
    const verb = r.status === "rejected" ? "Use" : "Roll back to";
    // eslint-disable-next-line no-alert
    if (!window.confirm(`${verb} the knowledge as of ${r.asOf}?\n\nIt is held there — each morning's rebuild is still made and checked, but not used — until you release the hold.`)) return;
    run(requestRollback, { similarJobs: r.ref.id, crewModel: r.crew.id }, "Requested — applied within about 15 minutes.");
  };
  const release = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Release the hold? The next morning's rebuild may be used again.")) return;
    run(requestRelease, {}, "Requested — applied within about 15 minutes.");
  };

  return (
    <div className="jev-root" ref={rootRef} style={{ display: "grid", gap: 12 }}>
      <section className="jev-card">
        <H label="What Jev knows" as="h3" />
        {data.hold ? (
          <div className="jev-note" style={{ marginTop: 6 }}>
            <span className="jev-help" data-tip={KNOWLEDGE_HELP.Hold}>On hold</span> since {fmt(data.hold.since)} (rolled back by {data.hold.by || "?"}).{" "}
            <button type="button" className="jev-btn" disabled={busy || Boolean(pending)} onClick={release}>Release the hold</button>
          </div>
        ) : null}
        {pending ? <div className="jev-note" style={{ marginTop: 6 }}>Waiting to be applied: {pending.kind === "rollback" ? `roll back to ${pending.similarJobs}` : "release the hold"} (asked {fmt(pending.requestedAt)}).</div> : null}
        {msg ? <div className="jev-note" style={{ marginTop: 6 }}>{msg}</div> : null}
      </section>
      <section className="jev-card">
        <H label="Current" />
        <VersionTable rows={current} />
      </section>
      <section className="jev-card">
        <H label="Previous" />
        <VersionTable rows={previous} action={pending ? null : rollback} busy={busy} />
      </section>
      <section className="jev-card">
        <H label="Held back" />
        <VersionTable rows={held} action={pending ? null : rollback} busy={busy} />
      </section>
      <section className="jev-card">
        <H label="Alerts" />
        {data.alerts.length ? (
          <ul>
            {[...data.alerts].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0)).map((a) => (
              <li key={a.id}>
                <span className="jev-note">{fmt(a.createdAt)}</span> {a.message}
              </li>
            ))}
          </ul>
        ) : (
          <div className="jev-empty">None.</div>
        )}
      </section>
      <section className="jev-card">
        <H label="Requests" />
        {data.requests.length ? (
          <ul>
            {data.requests.map((q) => (
              <li key={q.id}>
                <span className="jev-note">{fmt(q.requestedAt)}</span> {q.kind === "rollback" ? `Roll back to ${q.similarJobs}` : "Release the hold"} — {q.status}
                {q.result ? <span className="jev-note"> · {q.result}</span> : null}
              </li>
            ))}
          </ul>
        ) : (
          <div className="jev-empty">None yet.</div>
        )}
      </section>
      <Tooltip rootRef={rootRef} ready={true} />
    </div>
  );
}
